import React, { useEffect, useMemo } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';

import { ANALYTICS_TYPES } from '../../analytics';
import { EXO_SKELETON_SCOPE_NAME } from '../../consts';
import { formatDate } from '../../utils/formatDate';
import FlexCentered from '../FlexCentered';

export type AnnouncementBannerProps = {
  text: string;
  startDate?: string;
  endDate?: string;
};

export const AnnouncementBanner = ({
  text = '',
  startDate,
  endDate,
}: AnnouncementBannerProps) => {
  useEffect(() => {
    if (text) {
      report({
        scope: EXO_SKELETON_SCOPE_NAME,
        type: REPORT_TYPES.info,
        value: 'Announcement Banner is shown',
        context: {
          text,
          startDate,
          endDate,
        },
      });
      analytics.track(ANALYTICS_TYPES.ANNOUNCMENT_BANNER_VIEW, {
        text,
        startDate,
        endDate,
      });
    }
  }, [endDate, startDate, text]);

  const formattedStartDate = useMemo(
    () =>
      startDate
        ? formatDate(
            startDate,
            'Announcement Banner - Error in formatting startDate',
          )
        : undefined,
    [startDate],
  );

  const formattedEndDate = useMemo(
    () =>
      endDate
        ? formatDate(
            endDate,
            'Announcement Banner - Error in formatting endDate',
          )
        : undefined,
    [endDate],
  );

  const announcementText = useMemo(() => {
    const resolvedText = text ? `${text} ` : '';
    const resolvedStartDate = formattedStartDate
      ? `from ${formattedStartDate} `
      : '';
    const resolvedEndDate = formattedEndDate
      ? `until ${formattedEndDate} `
      : '';

    return resolvedText + resolvedStartDate + resolvedEndDate;
  }, [text, formattedStartDate, formattedEndDate]);

  if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
    report({
      scope: EXO_SKELETON_SCOPE_NAME,
      type: REPORT_TYPES.warning,
      value: 'Announcement Banner - endDate was set to be before startDate',
      context: {
        startDate,
        endDate,
      },
    });
  }

  if (text === '') {
    report({
      scope: EXO_SKELETON_SCOPE_NAME,
      type: REPORT_TYPES.info,
      value: 'Text is empty banner will not be shown',
      context: {
        text,
        startDate,
        endDate,
      },
    });
    return null;
  }

  return (
    <FlexCentered
      padding="0 24px"
      data-testid="announcement-banner-wrapper"
      backgroundColor="yellowOrange100"
      height="32px"
      alignContent="center"
    >
      <OverflowTooltip
        triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
        getTargetProps={() => ({
          style: { width: '100%', textAlign: 'center' },
        })}
      >
        {announcementText}
      </OverflowTooltip>
    </FlexCentered>
  );
};
